import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useSite } from "@/context/SiteContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Waves, LogOut, MapPin, Clock, Package, CheckCircle2, Truck, Navigation, Camera, QrCode, Coins } from "lucide-react";
import { toast } from "sonner";
import DriverApplication from "@/pages/driver/DriverApplication";

async function resizePhoto(file) {
  return new Promise((resolve) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onload = () => { img.src = reader.result; };
    img.onload = () => {
      const scale = Math.min(1, 480 / Math.max(img.width, img.height));
      const c = document.createElement("canvas");
      c.width = img.width * scale; c.height = img.height * scale;
      c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
      resolve(c.toDataURL("image/jpeg", 0.6));
    };
    reader.readAsDataURL(file);
  });
}

export default function DriverDashboard() {
  const { user, logout } = useAuth();
  const { site } = useSite();
  const navigate = useNavigate();
  const [data, setData] = useState({ available: [], mine: [], completed: [] });
  const [stats, setStats] = useState({ completed: 0, earnings: 0, active: 0 });
  const [tab, setTab] = useState("available");
  const [coords, setCoords] = useState(null);
  const [profile, setProfile] = useState(null);
  const [radius, setRadius] = useState("");
  const [wallet, setWallet] = useState({ available: 0, payouts: [] });
  const [appStatus, setAppStatus] = useState("incomplete");

  const loadApp = () => api.get("/driver/application").then((r) => setAppStatus(r.data.application_status));
  const loadProfile = () => api.get("/driver/profile").then((r) => { setProfile(r.data); setRadius(r.data.radius_km || ""); });
  const loadWallet = () => api.get("/driver/payouts").then((r) => setWallet(r.data));

  const load = (c = coords) => {
    const params = c ? { lat: c.lat, lng: c.lng } : {};
    api.get("/driver/jobs", { params }).then((r) => setData(r.data));
    api.get("/driver/stats").then((r) => setStats(r.data));
    loadWallet();
  };
  useEffect(() => {
    loadProfile();
    loadApp();
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (p) => { const c = { lat: p.coords.latitude, lng: p.coords.longitude }; setCoords(c); load(c); },
        () => load(null)
      );
    } else load(null);
    const t = setInterval(() => load(), 20000);
    return () => clearInterval(t);
  }, []);

  const saveArea = async () => {
    try {
      const payload = { radius_km: radius === "" ? 0 : parseFloat(radius) };
      if (coords) { payload.lat = coords.lat; payload.lng = coords.lng; }
      await api.put("/driver/profile", payload);
      loadProfile(); load();
      toast.success("Your work area was saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const requestPayout = async () => {
    try { await api.post("/driver/payouts/request"); loadWallet(); toast.success("Payout requested — the laundrette will process it"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const accept = async (id) => {
    try { await api.post(`/driver/jobs/${id}/accept`); load(); toast.success("Job accepted!"); setTab("mine"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); load(); }
  };

  const list = tab === "available" ? data.available : tab === "mine" ? data.mine : data.completed;
  const name = site?.site_name || "Just Laundry";

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      <header className="sticky top-0 z-10 border-b border-slate-800 bg-slate-900/90 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-3xl items-center justify-between px-5">
          <Link to="/" className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand"><Waves className="h-4 w-4" strokeWidth={1.5} /></span>
            <span className="font-heading text-lg font-bold">{name} Driver</span>
          </Link>
          <Button variant="ghost" size="icon" className="text-slate-300 hover:bg-slate-800 hover:text-white" onClick={() => { logout(); navigate("/"); }} data-testid="driver-logout"><LogOut className="h-4 w-4" /></Button>
        </div>
      </header>

      <div className="mx-auto max-w-3xl px-5 py-8">
        <h1 className="font-heading text-3xl font-bold tracking-tight">Hi {user?.name?.split(" ")[0]} 👋</h1>
        <div className="mt-3 flex items-center gap-2 text-sm text-slate-400">
          <Navigation className="h-4 w-4" /> {coords ? "Sorted by distance from you" : "Enable location for distances"}
        </div>

        {profile && profile.status !== "approved" && (
          <DriverApplication status={appStatus} onDone={() => { loadProfile(); loadApp(); }} />
        )}

        {profile && profile.status === "approved" && (<>
        <div className="mt-5 grid grid-cols-3 gap-3">
          <div className="rounded-xl border border-slate-800 bg-slate-800/60 p-4"><p className="text-xs uppercase tracking-wide text-slate-400">Earnings</p><p className="mt-1 font-heading text-2xl font-bold text-green-400">£{stats.earnings.toFixed(2)}</p></div>
          <div className="rounded-xl border border-slate-800 bg-slate-800/60 p-4"><p className="text-xs uppercase tracking-wide text-slate-400">Completed</p><p className="mt-1 font-heading text-2xl font-bold">{stats.completed}</p></div>
          <div className="rounded-xl border border-slate-800 bg-slate-800/60 p-4"><p className="text-xs uppercase tracking-wide text-slate-400">Active</p><p className="mt-1 font-heading text-2xl font-bold">{stats.active}</p></div>
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <div className="rounded-xl border border-slate-800 bg-slate-800/60 p-4" data-testid="driver-area-card">
            <p className="flex items-center gap-2 text-sm font-semibold text-slate-200"><Navigation className="h-4 w-4" /> My work area</p>
            <p className="mt-1 text-xs text-slate-400">Only show jobs within this distance of your saved location. Leave blank for all jobs.</p>
            <div className="mt-3 flex items-center gap-2">
              <Input value={radius} onChange={(e) => setRadius(e.target.value)} type="number" placeholder="Radius km" className="h-9 bg-slate-900 text-white" data-testid="driver-radius-input" />
              <Button size="sm" className="bg-brand hover:bg-brand-hover" onClick={saveArea} data-testid="save-area-btn">Save</Button>
            </div>
            <p className="mt-2 text-xs text-slate-500">{coords ? "Using your current location." : "Enable location to set your area centre."}</p>
          </div>
          <div className="rounded-xl border border-slate-800 bg-slate-800/60 p-4" data-testid="driver-wallet-card">
            <p className="flex items-center gap-2 text-sm font-semibold text-slate-200"><Coins className="h-4 w-4" /> Wallet</p>
            <p className="mt-2 font-heading text-2xl font-bold text-green-400" data-testid="driver-balance">£{wallet.available.toFixed(2)}</p>
            <p className="text-xs text-slate-400">Available to withdraw</p>
            <Button size="sm" className="mt-3 w-full bg-brand hover:bg-brand-hover" disabled={wallet.available <= 0} onClick={requestPayout} data-testid="request-payout-btn">Request payout</Button>
            {wallet.payouts?.length > 0 && (
              <div className="mt-3 space-y-1" data-testid="payout-history">
                {wallet.payouts.slice(0, 3).map((p) => (
                  <div key={p.id} className="flex justify-between text-xs text-slate-400">
                    <span>£{p.amount.toFixed(2)} · {(p.requested_at || "").slice(0, 10)}</span>
                    <span className={p.status === "paid" ? "text-green-400" : "text-amber-400"}>{p.status}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 flex gap-2 rounded-xl bg-slate-800 p-1">
          {[["available", "Available", data.available.length], ["mine", "My jobs", data.mine.length], ["completed", "Done", data.completed.length]].map(([k, label, count]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`driver-tab-${k}`}
              className={`flex-1 rounded-lg px-3 py-2.5 text-sm font-semibold transition-all ${tab === k ? "bg-brand text-white" : "text-slate-300 hover:text-white"}`}>
              {label} <span className="opacity-70">({count})</span>
            </button>
          ))}
        </div>

        <div className="mt-6 space-y-4">
          {list.length === 0 && (
            <div className="rounded-xl border border-dashed border-slate-700 bg-slate-800/50 p-12 text-center text-slate-400" data-testid="driver-empty">
              <Truck className="mx-auto h-10 w-10 text-slate-600" />
              <p className="mt-3">{tab === "available" ? "No jobs available right now." : "Nothing here yet."}</p>
            </div>
          )}
          {list.map((j) => <JobCard key={j.id} job={j} tab={tab} onAccept={accept} reload={load} />)}
        </div>
        </>)}
      </div>
    </div>
  );
}

function JobCard({ job, tab, onAccept, reload }) {
  const fileRef = useRef();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const isDelivery = job.job_type === "delivery";

  const call = async (action, body = {}) => {
    setBusy(true);
    try { await api.post(`/driver/jobs/${job.id}/${action}`, body); reload(); toast.success("Updated"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const onPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const photo = await resizePhoto(f);
    await call("arrive", { photo });
  };

  return (
    <div className="rounded-xl border border-slate-700 bg-slate-800 p-6" data-testid={`driver-job-${job.id}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <Badge className="border-0 bg-brand/20 text-blue-300 capitalize">{isDelivery ? "Delivery" : "Collection"}</Badge>
          {job.status !== "available" && <Badge className="border-0 bg-slate-700 capitalize text-slate-200">{job.status.replace(/_/g, " ")}</Badge>}
        </div>
        <div className="text-right">
          <p className="font-heading text-lg font-bold text-green-400">£{(job.fee ?? 3.5).toFixed(2)}</p>
          {job.distance_km != null && <p className="text-xs text-slate-400">{job.distance_km} km</p>}
        </div>
      </div>
      <p className="mt-3 font-heading text-lg font-medium">{job.laundry_name}</p>
      <div className="mt-2 space-y-1.5 text-sm text-slate-300">
        <p className="flex items-center gap-2"><QrCode className="h-4 w-4 text-slate-500" /> Ref: <span className="font-mono">{job.order_ref}</span></p>
        <p className="flex items-center gap-2"><Package className="h-4 w-4 text-slate-500" /> {job.customer_name}</p>
        <p className="flex items-center gap-2"><MapPin className="h-4 w-4 text-slate-500" /> {job.address}</p>
        <p className="flex items-center gap-2"><Clock className="h-4 w-4 text-slate-500" /> {job.pickup_date} · {job.pickup_slot}</p>
      </div>
      {job.photo && <img src={job.photo} alt="proof" className="mt-3 h-24 rounded-lg border border-slate-700 object-cover" />}

      <div className="mt-5 space-y-2">
        {tab === "available" && <Button className="w-full bg-brand text-base hover:bg-brand-hover" disabled={busy} onClick={() => onAccept(job.id)} data-testid={`accept-job-${job.id}`}>Accept job</Button>}

        {tab === "mine" && job.status === "accepted" && (
          <>
            <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onPhoto} data-testid={`photo-input-${job.id}`} />
            <Button className="w-full bg-slate-700 text-base hover:bg-slate-600" disabled={busy} onClick={() => fileRef.current?.click()} data-testid={`arrive-job-${job.id}`}><Camera className="mr-2 h-4 w-4" /> Arrived — take photo</Button>
          </>
        )}

        {tab === "mine" && job.status === "arrived" && (
          <div className="space-y-2">
            <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder={`Scan / enter QR code (${job.order_ref})`} className="bg-slate-900 text-white" data-testid={`code-input-${job.id}`} />
            <Button className="w-full bg-green-600 text-base hover:bg-green-700" disabled={busy} onClick={() => call("collect", { code })} data-testid={`collect-job-${job.id}`}><QrCode className="mr-2 h-4 w-4" /> {isDelivery ? "Confirm & set out" : "Confirm collection"}</Button>
          </div>
        )}

        {tab === "mine" && job.status === "collected" && (
          <Button className="w-full bg-green-600 text-base hover:bg-green-700" disabled={busy} onClick={() => call("complete")} data-testid={`complete-job-${job.id}`}><CheckCircle2 className="mr-2 h-4 w-4" /> {isDelivery ? "Mark delivered" : "Dropped at laundry"}</Button>
        )}

        {tab === "completed" && <p className="text-center text-sm font-medium text-green-400">Completed ✓ · earned £{(job.fee ?? 3.5).toFixed(2)}</p>}
      </div>
    </div>
  );
}
